import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DUAModal, { useDUACheck } from '../components/DUAModal';

const mono = "'JetBrains Mono',monospace";
const sans = "'DM Sans','Segoe UI',system-ui,sans-serif";

const colors = {
  bg: '#0a0f1e', card: 'rgba(15,23,42,0.5)', border: '#1e293b',
  text: '#e2e8f0', muted: '#64748b', dim: '#475569',
  blue: '#3b82f6', green: '#22c55e', amber: '#f59e0b', red: '#ef4444',
  blueBg: 'rgba(59,130,246,0.06)', greenBg: 'rgba(34,197,94,0.06)'
};

function Upload() {
  const { needsDUA, checked: duaChecked, refresh: refreshDUA } = useDUACheck();
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [campaignArms, setCampaignArms] = useState([]);
  const [selectedArm, setSelectedArm] = useState('');
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const getStep = () => {
    if (result) return 3;
    if (preview || confirming) return 2;
    if (uploading) return 1;
    return 0;
  };

  useEffect(() => {
    axios.get('/api/v2/campaigns').then(r => {
      const camps = r.data.campaigns || [];
      setCampaigns(camps);
      if (camps.length) { setSelectedCampaign(camps[0].slug); loadArms(camps[0].slug); }
    });
  }, []);

  const loadArms = async (slug) => {
    try {
      const r = await axios.get('/api/v2/campaigns/' + slug);
      const arms = r.data.arms || [];
      setCampaignArms(arms);
      setSelectedArm(arms.length ? arms[0].slug : '');
    } catch (e) { setCampaignArms([]); setSelectedArm(''); }
  };

  const handleCampaignChange = (slug) => {
    setSelectedCampaign(slug); setPreview(null); setMappings([]); setResult(null); setError('');
    loadArms(slug);
  };

  const handleDrag = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(e.type === 'dragenter' || e.type === 'dragover'); };
  const handleDrop = (e) => { e.preventDefault(); setDragActive(false); if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]); };

  const handleUpload = async () => {
    if (!file || !selectedCampaign) return;
    setError(''); setUploading(true); setResult(null);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('campaign_slug', selectedCampaign);
      const res = await axios.post('/api/v2/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data); setMappings(res.data.mappings);
      if (res.data.arms && res.data.arms.length) { setCampaignArms(res.data.arms); if (!selectedArm) setSelectedArm(res.data.arms[0].slug); }
    } catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setUploading(false); }
  };

  const updateMapping = (i, field, value) => { const m = [...mappings]; m[i] = { ...m[i], [field]: value }; setMappings(m); };

  const handleConfirm = async () => {
    setError(''); setConfirming(true);
    try {
      if (preview.isFollowFr) {
        // Follow.fr: skip mapping step, just send arm selection with confirm
        const res = await axios.post('/api/v2/upload/' + preview.upload_id + '/confirm', { target_arm: selectedArm || null });
        setResult(res.data); setPreview(null); setFile(null); setMappings([]);
      } else {
        await axios.put('/api/v2/upload/' + preview.upload_id + '/mapping', { mappings, target_arm: selectedArm || null });
        const res = await axios.post('/api/v2/upload/' + preview.upload_id + '/confirm');
        setResult(res.data); setPreview(null); setFile(null); setMappings([]);
      }
    } catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setConfirming(false); }
  };

  const handleReset = () => { setFile(null); setPreview(null); setMappings([]); setResult(null); setError(''); };

  const mapped = mappings.filter(m => m.target);
  const unmapped = mappings.filter(m => !m.target && !m.blocked);
  const blocked = mappings.filter(m => m.blocked);

  if (needsDUA) return (<div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:colors.bg}}><DUAModal onSigned={refreshDUA} /></div>);
  if (!duaChecked) return null;

  const stepIndex = getStep();
  const steps = ['Upload', 'Mapping', 'Confirm', 'Done'];

  return (
    <div style={{minHeight:'100vh',background:colors.bg,fontFamily:sans,color:colors.text,padding:'24px 20px'}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
        @keyframes spin { to { transform:rotate(360deg); } }
        .dark-select { width:100%;padding:10px 14px;border:1.5px solid ${colors.border};border-radius:10px;font-size:14px;font-family:${sans};background:#0f172a;color:${colors.text};outline:none; }
        .dark-select:focus { border-color:${colors.blue}; }
        .dark-field-select { padding:6px 8px;border:1px solid ${colors.border};border-radius:6px;font-size:12px;font-family:${sans};min-width:180px;background:#0f172a;color:${colors.text}; }
      `}</style>

      <div style={{maxWidth:860,margin:'0 auto'}}>
        {/* Header */}
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
          <div style={{width:8,height:8,background:colors.blue,transform:'rotate(45deg)'}} />
          <span style={{fontSize:12,fontWeight:600,color:colors.muted,letterSpacing:1.5,textTransform:'uppercase'}}>SpineBase</span>
        </div>
        <h1 style={{fontSize:22,fontWeight:700,color:'#f1f5f9',margin:'8px 0 4px'}}>Dataset Upload</h1>
        <p style={{fontSize:13,color:colors.muted,margin:'0 0 20px'}}>Drop your file — we auto-detect new patients vs. follow-up updates</p>

        {/* Progress */}
        <div style={{display:'flex',gap:3,marginBottom:28}}>
          {steps.map((s, i) => (
            <div key={s} style={{flex:1}}>
              <div style={{height:3,borderRadius:2,background:i<=stepIndex?colors.blue:colors.border,transition:'background 0.5s'}} />
              <div style={{fontSize:10,color:i<=stepIndex?'#93c5fd':colors.dim,marginTop:6,fontWeight:500,letterSpacing:0.5}}>{s}</div>
            </div>
          ))}
        </div>

        {/* ─── UPLOAD ─── */}
        {!preview && !result && !uploading && (
          <div style={{background:colors.card,border:'1px solid '+colors.border,borderRadius:16,padding:'24px'}}>
            <div style={{marginBottom:16}}>
              <label style={{display:'block',fontSize:11,fontWeight:600,textTransform:'uppercase',color:colors.muted,marginBottom:6,letterSpacing:0.5}}>Target Campaign</label>
              <select className="dark-select" value={selectedCampaign} onChange={e => handleCampaignChange(e.target.value)}>
                {campaigns.map(c => <option key={c.slug} value={c.slug}>{c.name} ({c.patient_count} patients)</option>)}
              </select>
            </div>

            {campaignArms.length > 1 && (
              <div style={{marginBottom:16}}>
                <label style={{display:'block',fontSize:11,fontWeight:600,textTransform:'uppercase',color:colors.muted,marginBottom:6,letterSpacing:0.5}}>Target Arm</label>
                <select className="dark-select" value={selectedArm} onChange={e => setSelectedArm(e.target.value)}>
                  {campaignArms.map(a => <option key={a.slug} value={a.slug}>{a.name}</option>)}
                </select>
              </div>
            )}

            <div
              onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop}
              onClick={() => document.getElementById('file-input').click()}
              style={{
                border:'2px dashed '+(dragActive?colors.blue:'#334155'),borderRadius:14,padding:'48px 24px',textAlign:'center',
                cursor:'pointer',background:dragActive?colors.blueBg:'rgba(15,23,42,0.3)',transition:'all 0.2s'
              }}>
              <input id="file-input" type="file" accept=".csv,.xlsx,.xls,.tsv" style={{display:'none'}}
                onChange={e => { if(e.target.files[0]) setFile(e.target.files[0]); }} />
              {file ? (
                <div style={{display:'flex',alignItems:'center',gap:10,justifyContent:'center'}}>
                  <span style={{fontSize:16}}>📄</span>
                  <span style={{fontWeight:600,color:colors.blue}}>{file.name}</span>
                  <span style={{fontSize:12,color:colors.muted}}>{(file.size/1024).toFixed(1)} KB</span>
                  <button onClick={e => {e.stopPropagation();setFile(null);}} style={{background:'none',border:'none',color:colors.red,fontSize:18,cursor:'pointer'}}>&times;</button>
                </div>
              ) : (
                <>
                  <div style={{fontSize:32,opacity:0.4,marginBottom:8,color:colors.muted}}>+</div>
                  <div style={{fontSize:15,fontWeight:600,marginBottom:4}}>Drop your dataset here</div>
                  <div style={{fontSize:12,color:colors.muted}}>Same file with follow-up columns added — or a new dataset</div>
                  <div style={{fontSize:11,color:colors.dim,marginTop:12}}>.xlsx, .csv, .xls — any format from your EHR or spreadsheet</div>
                </>
              )}
            </div>

            {file && (
              <button onClick={handleUpload} disabled={uploading}
                style={{display:'block',margin:'16px auto 0',padding:'10px 28px',background:colors.blue,color:'#fff',border:'none',borderRadius:10,fontSize:13,fontWeight:600,cursor:'pointer'}}>
                Analyze & Preview Mapping
              </button>
            )}
          </div>
        )}

        {/* ─── ANALYZING ─── */}
        {uploading && (
          <div style={{background:colors.card,border:'1px solid '+colors.border,borderRadius:16,padding:'48px 24px',textAlign:'center'}}>
            <div style={{width:36,height:36,border:'3px solid '+colors.border,borderTopColor:colors.blue,borderRadius:'50%',margin:'0 auto 14px',animation:'spin 0.8s linear infinite'}} />
            <div style={{fontSize:15,fontWeight:600,marginBottom:4}}>Analyzing your dataset...</div>
            <div style={{fontSize:12,color:colors.muted}}>Detecting columns, matching patients, checking PHI</div>
          </div>
        )}

        {error && <div style={{margin:'12px 0',padding:'10px 14px',background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:10,color:colors.red,fontSize:13}}>{error}</div>}

        {/* ─── FOLLOW.FR CONFIRMATION (simplified) ─── */}
        {preview && !result && preview.isFollowFr && (
          <div style={{background:colors.card,border:'1px solid '+colors.border,borderRadius:16,padding:'24px'}}>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:18}}>
              <div style={{width:40,height:40,borderRadius:10,background:'rgba(34,197,94,0.08)',border:'1.5px solid rgba(34,197,94,0.25)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20}}>
                <span role="img" aria-label="check">&#x2705;</span>
              </div>
              <div>
                <h2 style={{fontSize:17,fontWeight:700,margin:'0 0 2px'}}>Follow.fr Auto-Mapping Complete</h2>
                <p style={{fontSize:13,color:colors.green,margin:'2px 0 4px',fontWeight:600}}>{preview.follow_fr_summary.tlif_count} TLIF patients found out of {preview.follow_fr_summary.total_rows} total</p>
                <p style={{fontSize:12,color:colors.muted,margin:0}}>{preview.filename} &middot; {preview.row_count} patients &middot; {preview.column_count} source columns</p>
              </div>
            </div>

            {/* Summary cards */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:18}}>
              <div style={{background:'rgba(34,197,94,0.06)',border:'1px solid rgba(34,197,94,0.18)',borderRadius:12,padding:'14px 16px'}}>
                <div style={{fontSize:28,fontWeight:700,color:colors.green,fontFamily:mono}}>{preview.follow_fr_summary.mapped_fields}</div>
                <div style={{fontSize:12,fontWeight:600,color:'#86efac',marginTop:2}}>Fields mapped automatically</div>
                <div style={{fontSize:11,color:colors.muted,marginTop:4}}>Demographics, surgical data, ODI, VAS, satisfaction, follow-ups</div>
              </div>
              <div style={{background:'rgba(59,130,246,0.06)',border:'1px solid rgba(59,130,246,0.18)',borderRadius:12,padding:'14px 16px'}}>
                <div style={{fontSize:28,fontWeight:700,color:colors.blue,fontFamily:mono}}>{preview.follow_fr_summary.raw_follow_fields}</div>
                <div style={{fontSize:12,fontWeight:600,color:'#93c5fd',marginTop:2}}>Additional fields preserved as raw data</div>
                <div style={{fontSize:11,color:colors.muted,marginTop:4}}>Stored in JSONB for future analysis &mdash; zero data loss</div>
              </div>
            </div>

            {/* Ambiguous fields that need surgeon input */}
            {preview.follow_fr_summary.ambiguous_fields.length > 0 && (
              <div style={{background:'rgba(245,158,11,0.04)',border:'1px solid rgba(245,158,11,0.18)',borderRadius:12,padding:'14px 16px',marginBottom:18}}>
                <div style={{fontSize:13,fontWeight:600,color:'#fcd34d',marginBottom:10}}>Please confirm:</div>
                {preview.follow_fr_summary.ambiguous_fields.map(af => (
                  <div key={af.field} style={{marginBottom:af.field === 'arm' ? 10 : 6}}>
                    {af.field === 'arm' && (
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <label style={{fontSize:12,color:'#fcd34d',fontWeight:500,whiteSpace:'nowrap'}}>Arm:</label>
                        <select className="dark-field-select" value={selectedArm} onChange={e => setSelectedArm(e.target.value)}
                          style={{flex:1,padding:'8px 12px',fontSize:13,borderRadius:8,border:'1.5px solid rgba(245,158,11,0.3)',background:'#0f172a',color:colors.text}}>
                          {af.options.map(a => <option key={a.slug} value={a.slug}>{a.name}</option>)}
                        </select>
                      </div>
                    )}
                    {af.field === 'surgery_date' && (
                      <div style={{fontSize:12,color:'#fcd34d',display:'flex',alignItems:'center',gap:6}}>
                        <span style={{fontSize:14}}>&#x26A0;&#xFE0F;</span> {af.note}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Arm selector even if not ambiguous but multiple arms exist */}
            {preview.follow_fr_summary.ambiguous_fields.every(af => af.field !== 'arm') && campaignArms.length > 1 && (
              <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'rgba(59,130,246,0.06)',border:'1px solid rgba(59,130,246,0.15)',borderRadius:10,marginBottom:18}}>
                <label style={{fontSize:12,color:'#93c5fd',fontWeight:500,whiteSpace:'nowrap'}}>Assign to arm:</label>
                <select className="dark-field-select" value={selectedArm} onChange={e => setSelectedArm(e.target.value)}>
                  {campaignArms.map(a => <option key={a.slug} value={a.slug}>{a.name}</option>)}
                </select>
              </div>
            )}

            <div style={{display:'flex',justifyContent:'space-between',gap:10}}>
              <button onClick={handleReset} style={{padding:'10px 20px',background:'transparent',border:'1px solid #334155',color:colors.muted,borderRadius:10,fontSize:13,fontWeight:500,cursor:'pointer'}}>
                &larr; Back
              </button>
              <button onClick={handleConfirm} disabled={confirming}
                style={{padding:'12px 32px',background:confirming?'#334155':'linear-gradient(135deg,#22c55e,#16a34a)',border:'none',color:'#fff',borderRadius:12,fontSize:15,fontWeight:700,cursor:confirming?'not-allowed':'pointer',boxShadow:confirming?'none':'0 4px 14px rgba(34,197,94,0.3)'}}>
                {confirming ? 'Processing...' : 'Confirm & Upload ' + preview.row_count + ' Patients'}
              </button>
            </div>
          </div>
        )}

        {/* ─── STANDARD MAPPING (non-Follow.fr) ─── */}
        {preview && !result && !preview.isFollowFr && (
          <div style={{background:colors.card,border:'1px solid '+colors.border,borderRadius:16,padding:'20px 24px'}}>
            {blocked.length > 0 && (
              <div style={{display:'flex',gap:10,padding:'10px 14px',background:'rgba(239,68,68,0.06)',border:'1px solid rgba(239,68,68,0.15)',borderRadius:10,marginBottom:14}}>
                <span style={{fontSize:20}}>&#x1F6E1;&#xFE0F;</span>
                <div>
                  <strong style={{color:'#fca5a5',fontSize:13}}>PHI Protection Active</strong>
                  <p style={{color:'#f87171',fontSize:11,margin:'2px 0 0'}}>{blocked.length} column{blocked.length!==1?'s':''} blocked</p>
                </div>
              </div>
            )}

            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:14,flexWrap:'wrap',gap:10}}>
              <div>
                <h2 style={{fontSize:16,fontWeight:700,margin:'0 0 3px'}}>Field Mapping Preview</h2>
                <p style={{fontSize:12,color:colors.muted,margin:0}}>{preview.filename} &rarr; {preview.campaign} &middot; {preview.row_count} rows</p>
              </div>
              <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                <span style={{padding:'2px 8px',background:'rgba(34,197,94,0.1)',color:'#86efac',borderRadius:10,fontSize:11,fontWeight:600}}>{mapped.length} mapped</span>
                {unmapped.length > 0 && <span style={{padding:'2px 8px',background:'rgba(245,158,11,0.1)',color:'#fcd34d',borderRadius:10,fontSize:11,fontWeight:600}}>{unmapped.length} unmapped</span>}
                {blocked.length > 0 && <span style={{padding:'2px 8px',background:'rgba(239,68,68,0.1)',color:'#fca5a5',borderRadius:10,fontSize:11,fontWeight:600}}>{blocked.length} blocked</span>}
              </div>
            </div>

            {campaignArms.length > 1 && (
              <div style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:'rgba(59,130,246,0.06)',border:'1px solid rgba(59,130,246,0.15)',borderRadius:8,marginBottom:14}}>
                <label style={{fontSize:12,color:'#93c5fd',fontWeight:500,whiteSpace:'nowrap'}}>Assign to arm:</label>
                <select className="dark-field-select" value={selectedArm} onChange={e => setSelectedArm(e.target.value)}>
                  {campaignArms.map(a => <option key={a.slug} value={a.slug}>{a.name}</option>)}
                </select>
              </div>
            )}

            <div style={{overflowX:'auto',marginBottom:14}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead>
                  <tr style={{borderBottom:'1px solid '+colors.border}}>
                    {['Your Column','Type','','SpineBase Field','Transform','Conf.'].map(h => (
                      <th key={h} style={{padding:'8px 8px',textAlign:'left',color:colors.muted,fontWeight:500,fontSize:10,letterSpacing:0.5,textTransform:'uppercase'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mappings.map((m, i) => (
                    <tr key={i} style={{borderBottom:'1px solid rgba(30,41,59,0.5)',background:m.blocked?'rgba(239,68,68,0.03)':m.target?'rgba(59,130,246,0.02)':'transparent'}}>
                      <td style={{padding:'7px 8px',fontWeight:600,color:m.blocked?'#fca5a5':'#e2e8f0',whiteSpace:'nowrap',fontSize:12}}>
                        {m.source}{m.blocked && <span style={{background:colors.red,color:'#fff',fontSize:9,padding:'1px 5px',borderRadius:3,marginLeft:6,fontWeight:600}}>PHI</span>}
                      </td>
                      <td style={{padding:'7px 8px',fontFamily:mono,fontSize:10,color:colors.dim}}>{m.sourceType}</td>
                      <td style={{padding:'7px 8px',textAlign:'center',color:colors.dim,width:24}}>{m.blocked?'\uD83D\uDEAB':m.target?'\u2192':'\u00B7'}</td>
                      <td style={{padding:'7px 8px'}}>
                        {m.blocked ? <span style={{color:'#f87171',fontStyle:'italic',fontSize:11}}>Blocked &mdash; protected data</span> : (
                          <select className="dark-field-select" value={m.target||''} onChange={e => updateMapping(i,'target',e.target.value||null)}>
                            <option value="">&mdash; skip &mdash;</option>
                            {preview.schema_fields.map(f => <option key={f.field_name} value={f.field_name}>{f.field_label} ({f.data_type}){f.is_required?' *':''}</option>)}
                          </select>
                        )}
                      </td>
                      <td style={{padding:'7px 8px'}}>
                        {!m.blocked && m.transform !== 'none' && (
                          <span style={{fontSize:10,padding:'2px 6px',background:'rgba(124,58,237,0.1)',color:'#a78bfa',borderRadius:4}}>
                            {m.transform==='categorical_map'?'map':m.transform==='divide_by'?'\u00F7'+m.params?.divisor:m.transform==='boolean_map'?'y/n':m.transform==='date_format'?'date':m.transform}
                          </span>
                        )}
                      </td>
                      <td style={{padding:'7px 8px',textAlign:'center'}}>
                        {m.blocked ? <span style={{fontSize:10,padding:'2px 6px',background:'rgba(239,68,68,0.1)',color:'#fca5a5',borderRadius:4}}>blocked</span> :
                         m.confidence > 0 && <span style={{fontSize:10,padding:'2px 6px',borderRadius:4,fontWeight:600,
                          background:m.confidence>85?'rgba(34,197,94,0.1)':m.confidence>65?'rgba(245,158,11,0.1)':'rgba(239,68,68,0.1)',
                          color:m.confidence>85?'#86efac':m.confidence>65?'#fcd34d':'#fca5a5'}}>{m.confidence}%</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {preview.missing_required?.length > 0 && (
              <div style={{padding:'8px 12px',background:'rgba(245,158,11,0.06)',border:'1px solid rgba(245,158,11,0.15)',borderRadius:8,color:'#fcd34d',fontSize:12,marginBottom:14}}>
                Missing required: {preview.missing_required.join(', ')}
              </div>
            )}

            <div style={{display:'flex',justifyContent:'space-between',gap:10}}>
              <button onClick={handleReset} style={{padding:'8px 18px',background:'transparent',border:'1px solid #334155',color:colors.muted,borderRadius:10,fontSize:13,fontWeight:500,cursor:'pointer'}}>&larr; Back</button>
              <button onClick={handleConfirm} disabled={confirming||mapped.length===0}
                style={{padding:'8px 24px',background:confirming||mapped.length===0?'#334155':colors.blue,border:'none',color:'#fff',borderRadius:10,fontSize:13,fontWeight:600,cursor:confirming?'not-allowed':'pointer'}}>
                {confirming ? 'Processing...' : 'Confirm & Upload '+preview.row_count+' Patients'}
              </button>
            </div>
          </div>
        )}

        {/* ─── RESULT ─── */}
        {result && (
          <div style={{background:colors.card,border:'1px solid '+colors.border,borderRadius:16,padding:'36px 24px'}}>
            <div style={{textAlign:'center',marginBottom:20}}>
              <div style={{width:48,height:48,borderRadius:'50%',background:'rgba(34,197,94,0.08)',border:'2px solid '+colors.green,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 12px',fontSize:22,color:colors.green}}>✓</div>
              <h2 style={{fontSize:18,fontWeight:700,margin:0}}>Upload Complete</h2>
            </div>

            {result.summary && (
              <div style={{background:'rgba(15,23,42,0.7)',border:'1px solid '+colors.border,borderLeft:'3px solid '+colors.blue,borderRadius:8,padding:'12px 14px',marginBottom:12,fontSize:13,lineHeight:1.45}}>
                {result.summary}
                {result.matched_by_serial > 0 && (
                  <div style={{fontSize:11,color:colors.muted,marginTop:4}}>
                    {result.matched_by_serial} matched by patient serial (upsert)
                  </div>
                )}
              </div>
            )}

            <div style={{display:'grid',gridTemplateColumns:'repeat('+(
              (result.inserted>0?1:0)+(result.updated>0?1:0)+(result.skipped>0?1:0)+((!(result.updated>0)&&!(result.skipped>0))?1:0)
            )+',1fr)',gap:10,marginBottom:16}}>
              {result.inserted > 0 && (
                <div style={{background:'rgba(15,23,42,0.7)',border:'1px solid '+colors.border,borderRadius:12,padding:'14px',position:'relative',overflow:'hidden'}}>
                  <div style={{position:'absolute',top:8,right:10,fontSize:18,opacity:0.12,color:colors.green}}>+</div>
                  <div style={{fontSize:26,fontWeight:700,color:colors.green,fontFamily:mono}}>{result.inserted}</div>
                  <div style={{fontSize:11,fontWeight:600,marginTop:2}}>New Patients</div>
                  <div style={{fontSize:10,color:colors.muted}}>added to database</div>
                </div>
              )}
              {(result.updated||0) > 0 && (
                <div style={{background:'rgba(15,23,42,0.7)',border:'1px solid '+colors.border,borderRadius:12,padding:'14px',position:'relative',overflow:'hidden'}}>
                  <div style={{position:'absolute',top:8,right:10,fontSize:18,opacity:0.12,color:colors.blue}}>↻</div>
                  <div style={{fontSize:26,fontWeight:700,color:colors.blue,fontFamily:mono}}>{result.updated}</div>
                  <div style={{fontSize:11,fontWeight:600,marginTop:2}}>Updated</div>
                  <div style={{fontSize:10,color:colors.muted}}>follow-up merged</div>
                </div>
              )}
              {(result.skipped||0) > 0 && (
                <div style={{background:'rgba(15,23,42,0.7)',border:'1px solid '+colors.border,borderRadius:12,padding:'14px',position:'relative',overflow:'hidden'}}>
                  <div style={{position:'absolute',top:8,right:10,fontSize:18,opacity:0.12,color:colors.muted}}>=</div>
                  <div style={{fontSize:26,fontWeight:700,color:colors.muted,fontFamily:mono}}>{result.skipped}</div>
                  <div style={{fontSize:11,fontWeight:600,marginTop:2}}>Unchanged</div>
                  <div style={{fontSize:10,color:colors.muted}}>no new data</div>
                </div>
              )}
              {!(result.updated>0) && !(result.skipped>0) && (
                <div style={{background:'rgba(15,23,42,0.7)',border:'1px solid '+colors.border,borderRadius:12,padding:'14px'}}>
                  <div style={{fontSize:26,fontWeight:700,color:colors.blue,fontFamily:mono}}>{result.campaign_total}</div>
                  <div style={{fontSize:11,fontWeight:600,marginTop:2}}>Campaign Total</div>
                </div>
              )}
            </div>

            {result.follow_up_reward && (
              <div style={{background:'linear-gradient(135deg,'+colors.blueBg+','+colors.greenBg+')',border:'1px solid rgba(59,130,246,0.15)',borderRadius:10,padding:'12px 16px',display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600}}>Follow-up bonus earned</div>
                  <div style={{fontSize:11,color:colors.muted,marginTop:1}}>{result.follow_up_reward.follow_ups_improved} patients with new timepoint data</div>
                </div>
                <div style={{fontFamily:mono,fontSize:18,fontWeight:700,color:colors.green}}>+{result.follow_up_reward.total_bonus} <span style={{fontSize:11,color:colors.muted}}>$SPINE</span></div>
              </div>
            )}

            <div style={{display:'flex',justifyContent:'center',gap:24,padding:'12px 0',borderTop:'1px solid '+colors.border,marginBottom:16}}>
              <div style={{textAlign:'center'}}>
                <div style={{fontSize:16,fontWeight:700,fontFamily:mono}}>{result.campaign_total}</div>
                <div style={{fontSize:10,color:colors.muted,textTransform:'uppercase',letterSpacing:0.4}}>Campaign Total</div>
              </div>
              <div style={{textAlign:'center'}}>
                <div style={{fontSize:16,fontWeight:700,fontFamily:mono}}>{Math.round(result.avg_completeness*100)}%</div>
                <div style={{fontSize:10,color:colors.muted,textTransform:'uppercase',letterSpacing:0.4}}>Avg Completeness</div>
              </div>
              {result.spine_reward && (
                <div style={{textAlign:'center'}}>
                  <div style={{fontSize:16,fontWeight:700,fontFamily:mono,color:colors.green}}>{result.spine_reward.spine_display}</div>
                  <div style={{fontSize:10,color:colors.muted,textTransform:'uppercase',letterSpacing:0.4}}>Upload Reward</div>
                </div>
              )}
            </div>

            {result.phi_values_blocked > 0 && (
              <div style={{background:'rgba(34,197,94,0.06)',border:'1px solid rgba(34,197,94,0.15)',padding:'8px 12px',borderRadius:8,fontSize:12,color:'#86efac',marginBottom:12}}>
                🛡️ {result.phi_values_blocked} PHI values blocked during import.
              </div>
            )}
            {result.error_count > 0 && (
              <div style={{padding:'10px 12px',background:'rgba(239,68,68,0.06)',borderRadius:8,marginBottom:12}}>
                <p style={{fontWeight:600,color:colors.red,fontSize:12,margin:'0 0 4px'}}>{result.error_count} warnings:</p>
                {result.errors.map((e, i) => <div key={i} style={{fontSize:11,color:'#fca5a5',padding:'1px 0'}}>{e}</div>)}
              </div>
            )}

            <div style={{textAlign:'center'}}>
              <button onClick={handleReset} style={{padding:'9px 22px',background:colors.border,border:'1px solid #334155',color:colors.text,borderRadius:10,fontSize:13,fontWeight:500,cursor:'pointer'}}>Upload Another File</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
export default Upload;
